import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  TextField,
} from "@mui/material";
import { green } from "@mui/material/colors";
import QRCode from "qrcode.react";
import qrcode from "qrcode";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";

function getTitleDefault(dsnm, rname) {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const h = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const dateTime = `${y}.${m}.${d} ${h}:${min}`;
  return `${dateTime} / ${(dsnm || "").trim()} / ${(rname || "").trim()}`.replace(/\/\s*\//g, "/").trim();
}

// QR 스캔 시 열릴 주소 = 현재 화면(프론트) 기준. localhost:3000 이면 /attend 도 localhost:3000 으로 연결됨
function getAttendBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return "";
}

function parseTitleParts(title) {
  const parts = String(title || "").split(/\s*\/\s*/);
  return { time: parts[0] || "", disaster: parts[1] || "", assembly: parts[2] || "" };
}
function combineTitleParts(time, disaster, assembly) {
  return `${(time || "").trim()} / ${(disaster || "").trim()} / ${(assembly || "").trim()}`.replace(/\/\s*\/\s*\/?/g, "/").trim();
}

export default function RdvAttendanceQRModal(props) {
  // siteId: 재난현장 모드 (rdvId 대신 사용). 둘 중 하나만 있으면 됨
  const { open, onClose, dsid, rdvId, siteId, rname, dsnm, onOpenAttendance, apiAddr } = props;
  const [title, setTitle] = useState("");
  const [timePart, setTimePart] = useState("");
  const [disasterPart, setDisasterPart] = useState("");
  const [assemblyPart, setAssemblyPart] = useState("");
  const attendBaseUrl = getAttendBaseUrl();

  const isSiteMode = siteId != null && siteId !== "";
  const locLabel = "근무지";
  // 근무지 = 선택한 현장/집결지 이름(rname). 재난현장 추가 시 현장명(예: 안마산방면), 집결지 시 집결지명
  const workplaceName = (rname || "").trim();

  // rdvId 또는 siteId 중 하나로 QR 파라미터 구성
  const idParam = isSiteMode
    ? `site_id=${siteId}`
    : `rdv_id=${rdvId}`;
  const baseParams = `dsid=${encodeURIComponent(dsid)}&${idParam}&dsnm=${encodeURIComponent(dsnm || "")}&rname=${encodeURIComponent(workplaceName)}`;

  // type=other: 일반인 스캔 시 소속 선택 없이 바로 등록창(그외인력 출퇴근) 실행
  const hasId = isSiteMode ? siteId != null : rdvId != null;
  const qrValue = open && dsid != null && hasId && attendBaseUrl
    ? `${attendBaseUrl}/qrattendmgr?${baseParams}&type=other`
    : "";

  useEffect(() => {
    if (open) {
      // 근무지 = 선택한 현장명(rname) 또는 집결지명(rname)
      const def = getTitleDefault(dsnm || "", workplaceName);
      setTitle(def);
      const { time, disaster, assembly } = parseTitleParts(def);
      setTimePart(time);
      setDisasterPart(disaster);
      setAssemblyPart(assembly);
    }
  }, [open, dsnm, rname, workplaceName]);

  const handlePrintPreview = () => {
    if (!qrValue) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("팝업이 차단되었습니다. 인쇄를 허용해 주세요.");
      return;
    }
    const printTitle = title.trim() || getTitleDefault(dsnm || "", workplaceName || "");
    const { time: pt, disaster: pd, assembly: pa } = parseTitleParts(printTitle);
    const qrSizePx = 380; // 220 * 1.5 → 330, 추가 확대

    qrcode.toDataURL(qrValue, { width: qrSizePx, margin: 1 })
      .then((qrDataUrl) => {
        const escapeHtml = (s) =>
          String(s)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
        const e = (x) => escapeHtml(String(x));
        const escapedQr = qrDataUrl.replace(/"/g, "&quot;");
        const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8">
  <title>인쇄 미리보기 - 지원인력 출퇴근 관리</title>
  <style>
    @page { size: A4; margin: 10mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; width: 100%; min-height: 100vh; font-family: "Noto Sans KR", "Malgun Gothic", sans-serif; }
    body { display: flex; justify-content: center; align-items: center; background: #f5f5f5; padding: 24px; }
    .print-modal {
      width: 100%;
      max-width: 560px;
      aspect-ratio: 210 / 297;
      max-height: 85vh;
      display: flex;
      flex-direction: column;
      background: #fff;
      border-radius: 4px;
      overflow: hidden;
      border: 5px solid #2e7d32;
      box-shadow: 0 8px 32px rgba(0,0,0,0.2);
    }
    .print-header {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px 12px 10px 12px;
      min-height: 52px;
      font-weight: bold;
      background-color: #2e7d32;
      color: #fff;
      font-size: 1.875rem;
      text-align: center;
      flex-shrink: 0;
    }
    .print-body {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 8px;
      min-height: 0;
    }
    .print-title-block {
      width: 90%;
      margin-bottom: 24px;
      padding: 8px 14px;
      font-size: 2rem;
      line-height: 1.6;
      border: 1px solid rgba(0, 0, 0, 0.23);
      border-radius: 4px;
      background: #fff;
      color: #000;
    }
    .print-qr-wrap {
      padding: 8px;
      margin-top: 8px;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 4px;
      background: #fff;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .print-qr-wrap img {
      display: block;
      width: ${qrSizePx}px;
      height: ${qrSizePx}px;
    }
    .print-qr-caption {
      margin-top: 4px;
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
      text-align: center;
    }
    @media print {
      html, body { background: #fff; margin: 0; padding: 0; }
      body { padding: 0; }
      .print-modal { box-shadow: none; max-width: 100%; max-height: none; }
      .print-header {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
    }
  </style>
</head>
<body>
  <div class="print-modal">
    <div class="print-header">지원인력 출퇴근 관리</div>
    <div class="print-body">
      <div class="print-title-block">
        시   간: ${e(pt)}<br>
        재난명: ${e(pd)}<br>
        근무지: ${e(pa)}
      </div>
      <div class="print-qr-wrap">
        <img src="${escapedQr}" alt="출퇴근 QR코드" />
      </div>
    </div>
  </div>
</body>
</html>`;
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 300);
      })
      .catch((err) => {
        console.error(err);
        alert("QR 코드 생성 중 오류가 발생했습니다.");
        printWindow.close();
      });
  };

  if (!open) return null;

  const modalWidth = 560; // PDF/인쇄 시 더 크게 캡처되도록
  const qrSize = Math.round(modalWidth * 0.78 * 0.8); // QR 80% 축소 (모달 폭의 62.4%)

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        id="rdv-attendance-modal-a4"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "95%",
          maxWidth: modalWidth,
          maxHeight: "85vh",
          aspectRatio: "210 / 297",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 24,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          border: "5px solid",
          borderColor: green[700],
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            px: 1.5,
            py: 1.3,
            minHeight: 52,
            fontWeight: "bold",
            backgroundColor: green[700],
            color: "white",
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "2rem", textAlign: "center", flex: 1, lineHeight: 1.2 }}>
            지원인력 출퇴근 관리
          </Typography>
          <Button
            size="small"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, color: "white", minWidth: 0 }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>

          <Box sx={{ p: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0, alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ width: "90%", flexShrink: 0, fontSize: "2rem", "& .MuiInputBase-input": { fontSize: "2rem" } }}>
            <Box sx={{ "& > div": { mb: 0.5 }, "& > div:last-of-type": { mb: 1 } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" sx={{ flexShrink: 0, minWidth: "4em", fontWeight: 500 }}>시   간:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={timePart}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTimePart(v);
                    setTitle(combineTitleParts(v, disasterPart, assemblyPart));
                  }}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" sx={{ flexShrink: 0, minWidth: "4em", fontWeight: 500 }}>재난명:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={disasterPart}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDisasterPart(v);
                    setTitle(combineTitleParts(timePart, v, assemblyPart));
                  }}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span" sx={{ flexShrink: 0, minWidth: "4em", fontWeight: 500 }}>{locLabel}:</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={assemblyPart}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAssemblyPart(v);
                    setTitle(combineTitleParts(timePart, disasterPart, v));
                  }}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          </Box>

          {qrValue && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, minHeight: 0, width: "100%" }}>
              <Box
                id="rdv-attendance-qr-wrapper"
                sx={{
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <QRCode value={qrValue} size={qrSize} level="M" />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: "center", flexShrink: 0 }}>
                재난·자원집결지 정보 포함
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 1.5, width: "100%" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrintPreview}
              disabled={!qrValue}
            >
              인쇄미리보기
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
